import { DriftClient, User } from '@drift-labs/sdk';
import { COMMON_UTILS } from '@drift/common';
import { dlog } from 'src/dev';

const TX_PREP_TIME_METRIC_START_TIME_CACHE = new Map<string, number>();
import { DriftWindow } from 'src/window/driftWindow';
import {
	DriftClientTxSignedHandler,
	PREVIOUS_TX_SIGNED_HANDLER,
	TxMetricsOpts,
} from '../types';

export const updateAfterTx = (driftClient: DriftClient, user: User) => {
	setTimeout(() => {
		user.fetchAccounts();
		driftClient.fetchAccounts();
	}, 400);
};

export async function sendTxAndRecordConfirmationTime<T>(
	driftClient: DriftClient,
	txPromise: T,
	onSigned?: DriftClientTxSignedHandler,
	opts?: TxMetricsOpts
) {
	let handler: DriftClientTxSignedHandler;
	const signaturePromise = new Promise<void>((resolve) => {
		handler = (signedTxs) => {
			resolve();
			onSigned?.(signedTxs);
		};
		addTxSignedEventHandler(driftClient, handler);
	});
	const txSignedEventEmitter = driftClient.metricsEventEmitter;

	const txSignatureTimePromise = COMMON_UTILS.timedPromise(signaturePromise);
	const txConfirmationTimePromise = COMMON_UTILS.timedPromise(txPromise);

	// @ts-ignore
	txPromise.catch(() => {
		removeTxSignedEventHandler(driftClient, handler);
	});

	if (opts?.measurePreSignTime) {
		let handler: () => void;
		const preSignPromise = new Promise<void>((resolve) => {
			handler = resolve;
			txSignedEventEmitter.on('preTxSigned', handler);
		});
		const preSignTimePromise = COMMON_UTILS.timedPromise(preSignPromise);
		preSignTimePromise
			.then((result) => {
				const cachedStartTime = TX_PREP_TIME_METRIC_START_TIME_CACHE.get(
					opts.measurePreSignTimeMetricKey ?? ''
				);
				const shouldAdjustMeasuredTime = !!cachedStartTime;

				const metricTime = shouldAdjustMeasuredTime
					? Date.now() - cachedStartTime
					: result.promiseTime;

				const metricKey = opts.measurePreSignTimeMetricKey ?? 'unlabelled';

				dlog(`ui_metrics`, `pre_sign_time_measurement ${metricTime}ms`, {
					metricKey,
					promiseTime: result.promiseTime,
					metricTime: metricTime,
					delta: metricTime - result.promiseTime,
					shouldAdjustMeasuredTime,
				});

				DriftWindow.metricsBus?.next({
					type: `transaction_prep_time`,
					value: {
						timeMs: metricTime,
						key: metricKey,
					},
				});
			})
			.finally(() => {
				if (handler) {
					txSignedEventEmitter.off('preTxSigned', handler);
				}
			});
	}

	Promise.all([txSignatureTimePromise, txConfirmationTimePromise]).then(
		(results) => {
			const txSignatureTime = results[0].promiseTime;
			const txConfirmationTime = results[1].promiseTime;

			const actualConfirmationTime = txConfirmationTime - txSignatureTime;

			if (actualConfirmationTime <= 0) {
				// Caught an edge case once where the txConfirmationTime was less than the txSignatureTime. Don't see how this is possible not how we can "gracefully" handle it, so just going to skip logging this transaction time.
				dlog(`ui_metrics`, `bug::confirmation_time_less_than_sig_time`, {
					txSignatureTime,
					txConfirmationTime,
					diff: actualConfirmationTime,
				});
				return;
			}

			const formattedPromiseTime = +(actualConfirmationTime / 1000).toFixed(1);

			dlog(
				`ui_metrics`,
				`confirmation_time_measurement ${formattedPromiseTime}s`,
				{
					txSignatureTime,
					txConfirmationTime,
					diff: actualConfirmationTime,
				}
			);

			DriftWindow.metricsBus?.next({
				type: 'transaction_confirmation_time',
				value: formattedPromiseTime,
			});

			removeTxSignedEventHandler(driftClient, handler);
		}
	);

	return txPromise;
}

const removeTxSignedEventHandler = (
	driftClient: DriftClient,
	handler: DriftClientTxSignedHandler
) => {
	driftClient.metricsEventEmitter.off('txSigned', handler);
	PREVIOUS_TX_SIGNED_HANDLER.current = undefined;
};

const addTxSignedEventHandler = (
	driftClient: DriftClient,
	handler: DriftClientTxSignedHandler
) => {
	if (PREVIOUS_TX_SIGNED_HANDLER.current) {
		removeTxSignedEventHandler(driftClient, PREVIOUS_TX_SIGNED_HANDLER.current);
	}

	driftClient.metricsEventEmitter.on('txSigned', handler);
	PREVIOUS_TX_SIGNED_HANDLER.current = handler;
};
